import { Content } from "@robe/core-types/content";
import { XcoreCore } from "XcoreCore";
import {
  collectContentIdsFromRichtextContentLinks,
  resolveContentsSlugsByIds,
  mapFetchedContentsSlugs,
  assignUnfetchedValuesToContentValues,
} from "./utils";

/**
 * Loop through all contents values. If any of the values are richtext values collect its contents IDs from `links` if there are any.
 * Filter out duplicate ids and fetch contents slugs for these IDs.
 * Assign fetched contents slugs to richtext values based on contents ids in `links`.
 */
export const resolveUnfetchedValuesToContentValuesForContents = async <T extends Partial<Pick<Content, "id" | "values" | "imageInfos">>>(
  contents: Array<T>,
  core: XcoreCore
) => {
  if (
    !contents ||
    !Array.isArray(contents) ||
    contents.length === 0 ||
    !process.env.NEXT_PUBLIC_FETCH_UNFETCHED_FIELDS_VALUES ||
    process.env.NEXT_PUBLIC_FETCH_UNFETCHED_FIELDS_VALUES === "false"
  ) {
    return contents;
  }

  const locales = Object.keys(core.locales);
  const linksContentIds: number[] = [];

  contents.forEach((c) => {
    if (c?.values) {
      linksContentIds.push(...collectContentIdsFromRichtextContentLinks(c.values, locales));
    }
  });

  const contentsSlugsFetched = await resolveContentsSlugsByIds(core, linksContentIds);

  const contentsSlugs = mapFetchedContentsSlugs(contentsSlugsFetched ?? []);

  return contents.map((content) =>
    content?.values && content.imageInfos
      ? {
        ...content,
        values: assignUnfetchedValuesToContentValues({
          contentsSlugs,
          locales,
          values: content.values,
          imageInfos: content.imageInfos,
        }),
      }
      : content
  );
};

/**
 * Loop through all content values. If any of the values are richtext values collect its contents IDs from `links` if there are any.
 * Filter out duplicate ids and fetch contents slugs for these IDs.
 * Assign fetched contents slugs to richtext values based on contents ids in `links`.
 * This function cannot be reused in `resolveRichtextContentLinksForContents()` because contents slugs EP would be called for each content in contents array
 * instead of once after all contents IDs from `links` are collected.
 */
export const resolveUnfetchedValuesToContentValuesForContent = async <T extends Partial<Pick<Content, "id" | "values" | "imageInfos">>>(
  content: T,
  core: XcoreCore
): Promise<T> => {
  if (!content?.values || !content?.imageInfos) {
    return content;
  }

  const locales = Object.keys(core.locales);
  const linksContentIds = collectContentIdsFromRichtextContentLinks(content.values, locales);

  const contentsSlugsFetched = await resolveContentsSlugsByIds(core, linksContentIds);

  const contentsSlugs = mapFetchedContentsSlugs(contentsSlugsFetched ?? []);

  return {
    ...content,
    values: assignUnfetchedValuesToContentValues({
      contentsSlugs,
      locales,
      values: content.values,
      imageInfos: content.imageInfos,
    }),
  };
};
